// GlobalSearchFilterComponent.tsx - category-agnostic multifilter used inside
// the "See All Results" search modal (mirrors trade). Unlike the page-level
// FilterComponent it has no item_group, does not touch the URL or scroll
// restore, and seeds its state from the search dropdown's selected filters +
// query. It reuses consumer's own multifilter components so the grid, facets
// and toolbar match the rest of the site.

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { useMutation } from 'react-query';
import { apiFilterProducts } from '../../../api/apiMultiFilter';
import ProductGrid from '../filter/components/ProductGrid';
import FilterSelection from '../filter/components/FilterSelection';
import FilterTags from '../filter/components/FilterTags';
import ProductSearch from '../filter/components/ProductSearch';
import {
  Product,
  MultiFilterApiResponse,
  MultiFilterMutationRequest,
  MultiFilterMutation,
} from '../../../types/Multifilter.types';

interface GlobalSearchFilterComponentProps {
  initialSearchQuery: string;
  initialFilters?: {
    ranges?: string[];
    design_styles?: string[];
    colours?: string[];
    brands?: string[];
    applications?: string[];
  };
  onClose?: () => void;
  onSearchQueryChange?: (query: string) => void;
}

const FILTER_FIELDS = [
  'application', 'brand', 'range', 'web_colour',
  'design_style', 'web_materials', 'web_shape', 'web_size',
  'web_type', 'web_standards', 'web_composition', 'web_performance', 'item_group',
];

function GlobalSearchFilterComponent({
                                       initialSearchQuery,
                                       initialFilters,
                                       onClose,
                                       onSearchQueryChange,
                                     }: GlobalSearchFilterComponentProps) {
  const abortControllerRef = useRef<AbortController | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  // Selected filter values (seeded from the search dropdown).
  const [applicationsList, setApplicationsList] = useState<string[]>(initialFilters?.applications || []);
  const [rangeList, setRangeList] = useState<string[]>(initialFilters?.ranges || []);
  const [colourList, setColourList] = useState<string[]>(initialFilters?.colours || []);
  const [designList, setDesignList] = useState<string[]>(initialFilters?.design_styles || []);
  const [typesList, setTypesList] = useState<string[]>([]);
  const [brandList, setBrandList] = useState<string[]>(initialFilters?.brands || []);
  const [materialList, setMaterialList] = useState<string[]>([]);
  const [standardsList, setStandardsList] = useState<string[]>([]);
  const [compositionList, setCompositionList] = useState<string[]>([]);
  const [performanceList, setPerformanceList] = useState<string[]>([]);
  const [sizeList, setSizeList] = useState<string[]>([]);
  const [shapeList, setShapeList] = useState<string[]>([]);

  // Cross-category extras (mirrors trade's PDF multifilter): a stock
  // "In stock" availability toggle and an item-group ("Category") filter.
  const [inStockOnly, setInStockOnly] = useState<boolean>(false);
  const inStockRef = useRef<boolean>(false);
  const [categoryList, setCategoryList] = useState<string[]>([]);
  const [availableCategories, setAvailableCategories] = useState<{ [key: string]: string }>({});

  // Available/filtered facet options (populated from each response).
  const [availableApplications, setAvailableApplications] = useState<string[]>([]);
  const [availableRanges, setAvailableRanges] = useState<string[]>([]);
  const [availableColours, setAvailableColours] = useState<string[]>([]);
  const [availableDesigns, setAvailableDesigns] = useState<string[]>([]);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [availableMaterials, setAvailableMaterials] = useState<string[]>([]);
  const [availableStandards, setAvailableStandards] = useState<string[]>([]);
  const [availableCompositions, setAvailableCompositions] = useState<string[]>([]);
  const [availablePerformances, setAvailablePerformances] = useState<string[]>([]);
  const [availableSizes, setAvailableSizes] = useState<string[]>([]);
  const [availableShapes, setAvailableShapes] = useState<string[]>([]);

  const [filteredRanges, setFilteredRanges] = useState<string[]>([]);
  const [filteredColours, setFilteredColours] = useState<string[]>([]);
  const [filteredDesigns, setFilteredDesigns] = useState<string[]>([]);
  const [filteredTypes, setFilteredTypes] = useState<string[]>([]);
  const [filteredBrands, setFilteredBrands] = useState<string[]>([]);
  const [filteredMaterials, setFilteredMaterials] = useState<string[]>([]);
  const [filteredStandards, setFilteredStandards] = useState<string[]>([]);
  const [filteredCompositions, setFilteredCompositions] = useState<string[]>([]);
  const [filteredPerformances, setFilteredPerformances] = useState<string[]>([]);
  const [filteredSizes, setFilteredSizes] = useState<string[]>([]);
  const [filteredShapes, setFilteredShapes] = useState<string[]>([]);

  // Grid / paging state.
  const [productItemCount, setProductItemCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [sortBySetting, setSortBySetting] = useState<string>('Alphabetical A-Z');
  const [itemsPerPage, setItemsPerPage] = useState<number>(27);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [accumulatedProducts, setAccumulatedProducts] = useState<Product[]>([]);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  // Set when a load-more request fails so the grid stops auto-retrying and
  // shows a manual retry instead. Cleared by the next filter/sort/page request.
  const [loadMoreFailed, setLoadMoreFailed] = useState<boolean>(false);
  const loadingMoreRef = useRef<boolean>(false);

  // Search state — read at request-build time so every fetch carries user_query.
  const [searchQuery, setSearchQuery] = useState<string>(initialSearchQuery);
  const searchQueryRef = useRef<string>(initialSearchQuery);

  const makeApiCall = useCallback(async (requestData: MultiFilterMutationRequest): Promise<MultiFilterApiResponse> => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const response = await apiFilterProducts(requestData);
      return response;
    } catch (error: any) {
      if (error.name === 'AbortError') throw error;
      console.error('API call failed:', error);
      throw error;
    }
  }, []);

  const getProductsMutation: MultiFilterMutation = useMutation<MultiFilterApiResponse, Error, MultiFilterMutationRequest>(
    makeApiCall,
    {
      onMutate: () => {
        setLoadMoreFailed(false);
      },
      onSuccess: (response) => {
        const filters = response.filters;
        setAvailableApplications(filters?.application || []);
        setAvailableRanges(filters?.range || []);
        setAvailableColours(filters?.web_colour || []);
        setAvailableDesigns(filters?.design_style || []);
        setAvailableTypes(filters?.web_type || []);
        setAvailableBrands(filters?.brand || []);
        setAvailableMaterials(filters?.web_materials?.filter((m: string) => m !== '-') || []);
        setAvailableStandards(filters?.web_standards?.filter((s: string) => s !== '-') || []);
        setAvailableCompositions(filters?.web_composition?.filter((c: string) => c !== '-') || []);
        setAvailablePerformances(filters?.web_performance?.filter((p: string) => p !== '-') || []);
        setAvailableSizes(filters?.web_size || []);
        setAvailableShapes(filters?.web_shape || []);
        setFilteredRanges(filters?.range || []);
        setFilteredColours(filters?.web_colour || []);
        setFilteredDesigns(filters?.design_style || []);
        setFilteredTypes(filters?.web_type || []);
        setFilteredBrands(filters?.brand || []);
        setFilteredMaterials(filters?.web_materials?.filter((m: string) => m !== '-') || []);
        setFilteredStandards(filters?.web_standards?.filter((s: string) => s !== '-') || []);
        setFilteredCompositions(filters?.web_composition?.filter((c: string) => c !== '-') || []);
        setFilteredPerformances(filters?.web_performance?.filter((p: string) => p !== '-') || []);
        setFilteredSizes(filters?.web_size || []);
        setFilteredShapes(filters?.web_shape || []);
        setAvailableCategories((filters as any)?.item_group_names || {});
        setProductItemCount(response.total_items_count);

        const isAll = itemsPerPage === -1;
        setTotalPages(isAll ? 1 : Math.ceil(response.total_items_count / (itemsPerPage || 27)));
        if (!isAll) setAccumulatedProducts([]);
      },
      onError: (error: Error) => {
        if (error.name !== 'AbortError') console.error('Error fetching products:', error);
      },
    }
  );

  // Build the filter object from the given overrides (falling back to state).
  // No item_group — this is a global, cross-category search.
  const buildFilterObject = useCallback((overrides: {
    applications?: string[];
    ranges?: string[];
    colours?: string[];
    designStyles?: string[];
    types?: string[];
    brands?: string[];
    materials?: string[];
    standards?: string[];
    compositions?: string[];
    performances?: string[];
    sizes?: string[];
    shapes?: string[];
    categories?: string[];
  } = {}): MultiFilterMutationRequest['filters'] => {
    const applicationsToUse = overrides.applications !== undefined ? overrides.applications : applicationsList;
    const rangesToUse       = overrides.ranges       !== undefined ? overrides.ranges       : rangeList;
    const coloursToUse      = overrides.colours      !== undefined ? overrides.colours      : colourList;
    const designsToUse      = overrides.designStyles !== undefined ? overrides.designStyles : designList;
    const typesToUse        = overrides.types        !== undefined ? overrides.types        : typesList;
    const brandsToUse       = overrides.brands       !== undefined ? overrides.brands       : brandList;
    const materialsToUse    = overrides.materials    !== undefined ? overrides.materials    : materialList;
    const standardsToUse    = overrides.standards    !== undefined ? overrides.standards    : standardsList;
    const compositionsToUse = overrides.compositions !== undefined ? overrides.compositions : compositionList;
    const performancesToUse = overrides.performances !== undefined ? overrides.performances : performanceList;
    const sizesToUse        = overrides.sizes        !== undefined ? overrides.sizes        : sizeList;
    const shapesToUse       = overrides.shapes       !== undefined ? overrides.shapes       : shapeList;
    const categoriesToUse   = overrides.categories   !== undefined ? overrides.categories   : categoryList;

    const filterObject: Record<string, string[]> = {};
    if (applicationsToUse.length > 0) filterObject.application = applicationsToUse;
    if (rangesToUse.length > 0)       filterObject.range = rangesToUse;
    if (coloursToUse.length > 0)      filterObject.web_colour = coloursToUse;
    if (designsToUse.length > 0)      filterObject.design_style = designsToUse;
    if (typesToUse.length > 0)        filterObject.web_type = typesToUse;
    if (brandsToUse.length > 0)       filterObject.brand = brandsToUse;
    if (materialsToUse.length > 0)    filterObject.web_materials = materialsToUse;
    if (standardsToUse.length > 0)    filterObject.web_standards = standardsToUse;
    if (compositionsToUse.length > 0) filterObject.web_composition = compositionsToUse;
    if (performancesToUse.length > 0) filterObject.web_performance = performancesToUse;
    if (sizesToUse.length > 0)        filterObject.web_size = sizesToUse;
    if (shapesToUse.length > 0)       filterObject.web_shape = shapesToUse;
    // Item groups selected in the Category filter scope the cross-category search.
    if (categoriesToUse.length > 0)   filterObject.item_group = categoriesToUse;

    return filterObject as unknown as MultiFilterMutationRequest['filters'];
  }, [
    applicationsList, rangeList, colourList, designList, typesList,
    brandList, materialList, standardsList, compositionList, performanceList, sizeList, shapeList, categoryList,
  ]);

  const buildRequest = useCallback((
    filterObject: MultiFilterMutationRequest['filters'],
    start: number,
    end: number,
    sort: string,
  ): MultiFilterMutationRequest => ({
    filters: filterObject,
    filter_fields: FILTER_FIELDS,
    main_filter_field: null,
    selection_fields: null,
    sort_by_setting: sort,
    off_setter: { start, end },
    only_published: true,
    only_promo: false,
    only_in_stock: inStockRef.current,
    user_query: searchQueryRef.current || undefined,
    limited: true,
  }), []);

  const applyFiltersImmediately = useCallback((overrides: {
    applications?: string[];
    ranges?: string[];
    colours?: string[];
    designStyles?: string[];
    types?: string[];
    brands?: string[];
    materials?: string[];
    standards?: string[];
    compositions?: string[];
    performances?: string[];
    sizes?: string[];
    shapes?: string[];
    categories?: string[];
  } = {}) => {
    const filterObject = buildFilterObject(overrides);
    const isAll = itemsPerPage === -1;
    getProductsMutation.mutate(buildRequest(filterObject, 0, isAll ? 50 : itemsPerPage, sortBySetting));
    setCurrentPage(1);
  }, [buildFilterObject, buildRequest, itemsPerPage, sortBySetting, getProductsMutation]);

  const handleCheckboxChange = (
    elem: string,
    setList: React.Dispatch<React.SetStateAction<string[]>>,
    list: string[],
    paramKey: string
  ) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    const updatedList = isChecked ? [...list, elem] : list.filter(item => item !== elem);
    setList(updatedList);

    switch (paramKey) {
      case 'colours': applyFiltersImmediately({ colours: updatedList }); break;
      case 'design_style': applyFiltersImmediately({ designStyles: updatedList }); break;
      case 'types': applyFiltersImmediately({ types: updatedList }); break;
      case 'brand': applyFiltersImmediately({ brands: updatedList }); break;
      case 'range': applyFiltersImmediately({ ranges: updatedList }); break;
      case 'material': applyFiltersImmediately({ materials: updatedList }); break;
      case 'standards': applyFiltersImmediately({ standards: updatedList }); break;
      case 'composition': applyFiltersImmediately({ compositions: updatedList }); break;
      case 'performance': applyFiltersImmediately({ performances: updatedList }); break;
      case 'sizes': applyFiltersImmediately({ sizes: updatedList }); break;
      case 'shapes': applyFiltersImmediately({ shapes: updatedList }); break;
    }
  };

  const handleRemoveFilter = (category: string, value: string) => {
    switch (category) {
      case 'colours': { const n = colourList.filter(i => i !== value); setColourList(n); applyFiltersImmediately({ colours: n }); break; }
      case 'design_style': { const n = designList.filter(i => i !== value); setDesignList(n); applyFiltersImmediately({ designStyles: n }); break; }
      case 'types': { const n = typesList.filter(i => i !== value); setTypesList(n); applyFiltersImmediately({ types: n }); break; }
      case 'brand': { const n = brandList.filter(i => i !== value); setBrandList(n); applyFiltersImmediately({ brands: n }); break; }
      case 'range': { const n = rangeList.filter(i => i !== value); setRangeList(n); applyFiltersImmediately({ ranges: n }); break; }
      case 'material': { const n = materialList.filter(i => i !== value); setMaterialList(n); applyFiltersImmediately({ materials: n }); break; }
      case 'standards': { const n = standardsList.filter(i => i !== value); setStandardsList(n); applyFiltersImmediately({ standards: n }); break; }
      case 'composition': { const n = compositionList.filter(i => i !== value); setCompositionList(n); applyFiltersImmediately({ compositions: n }); break; }
      case 'performance': { const n = performanceList.filter(i => i !== value); setPerformanceList(n); applyFiltersImmediately({ performances: n }); break; }
      case 'sizes': { const n = sizeList.filter(i => i !== value); setSizeList(n); applyFiltersImmediately({ sizes: n }); break; }
      case 'shapes': { const n = shapeList.filter(i => i !== value); setShapeList(n); applyFiltersImmediately({ shapes: n }); break; }
    }
  };

  const handleClearAllFilters = () => {
    setApplicationsList([]);
    setRangeList([]);
    setColourList([]);
    setDesignList([]);
    setTypesList([]);
    setBrandList([]);
    setMaterialList([]);
    setStandardsList([]);
    setCompositionList([]);
    setPerformanceList([]);
    setSizeList([]);
    setShapeList([]);
    setCategoryList([]);
    applyFiltersImmediately({
      applications: [], ranges: [], colours: [], designStyles: [], types: [],
      brands: [], materials: [], standards: [], compositions: [], performances: [], sizes: [], shapes: [], categories: [],
    });
  };

  const applyFiltersForApplications = (updatedApplications: string[]) => {
    setApplicationsList(updatedApplications);
    applyFiltersImmediately({ applications: updatedApplications });
  };

  // Availability ("In stock") toggle. Read from a ref at request-build time so
  // every subsequent fetch carries only_in_stock.
  const handleStockChange = (inStockOnly: boolean) => {
    inStockRef.current = inStockOnly;
    setInStockOnly(inStockOnly);
    applyFiltersImmediately();
  };

  // Category (item-group) selection.
  const handleCategoryChange = (updatedCategories: string[]) => {
    setCategoryList(updatedCategories);
    applyFiltersImmediately({ categories: updatedCategories });
  };

  const handleSearchChange = (query: string) => {
    searchQueryRef.current = query;
    setSearchQuery(query);
    if (onSearchQueryChange) onSearchQueryChange(query);
    applyFiltersImmediately();
  };

  const handleSortChange = (newSortValue: string) => {
    setSortBySetting(newSortValue);
    setCurrentPage(1);
    const isAll = itemsPerPage === -1;
    getProductsMutation.mutate(buildRequest(buildFilterObject(), 0, isAll ? 50 : itemsPerPage, newSortValue));
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    const isAll = newItemsPerPage === -1;
    setItemsPerPage(newItemsPerPage);
    setShowAll(isAll);
    setCurrentPage(1);
    setAccumulatedProducts([]);
    getProductsMutation.mutate(buildRequest(buildFilterObject(), 0, isAll ? 50 : newItemsPerPage, sortBySetting));
  };

  const handleLoadMore = async () => {
    // The ref (not the state) is the guard: two observer callbacks can land
    // before the loadingMore state has re-rendered.
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadMoreFailed(false);
    setLoadingMore(true);
    try {
      const currentProductCount = accumulatedProducts.length > 0
        ? accumulatedProducts.length
        : (getProductsMutation.data?.products?.length || 0);

      const response = await makeApiCall(
        buildRequest(buildFilterObject(), currentProductCount, currentProductCount + 50, sortBySetting)
      );

      setAccumulatedProducts(prev => {
        const existing = prev.length > 0 ? prev : (getProductsMutation.data?.products || []);
        return [...existing, ...response.products];
      });
    } catch (error) {
      console.error('Error loading more products:', error);
      setLoadMoreFailed(true);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  const paginate = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    const start = (page - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    setCurrentPage(page);
    getProductsMutation.mutate(buildRequest(buildFilterObject(), start, end, sortBySetting));
    if (anchorRef.current) anchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const nextPage = () => paginate(currentPage + 1);
  const previousPage = () => paginate(currentPage - 1);
  const lastPage = () => paginate(totalPages);
  const firstPage = () => paginate(1);

  // Initial load.
  useEffect(() => {
    const filterObject = buildFilterObject();
    getProductsMutation.mutate(buildRequest(filterObject, 0, itemsPerPage, sortBySetting));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: { xs: 'block', lg: 'grid' },
          gridTemplateColumns: { lg: '3fr 9fr' },
          columnGap: { lg: 3 },
          alignItems: 'start',
          width: '100%',
          padding: '1.5rem',
        }}
      >
        {/* Left: filter column */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            position: { lg: 'sticky' },
            top: { lg: '1.5rem' },
            alignSelf: 'start',
            maxHeight: { lg: 'calc(90vh - 120px)' },
            overflowY: { lg: 'auto' },
            pr: { lg: 1 },
            scrollbarGutter: 'stable',
            scrollbarWidth: 'thin',
          }}
        >
          <ProductSearch
            onSearch={handleSearchChange}
            isLoading={getProductsMutation.isLoading}
            initialValue={searchQuery}
          />

          <FilterTags
            rangeList={rangeList}
            colourList={colourList}
            designList={designList}
            typesList={typesList}
            brandList={brandList}
            materialList={materialList}
            standardsList={standardsList}
            compositionList={compositionList}
            performanceList={performanceList}
            sizeList={sizeList}
            shapeList={shapeList}
            onRemoveFilter={handleRemoveFilter}
            onClearAll={handleClearAllFilters}
          />

          <FilterSelection
            availableColours={availableColours}
            availableDesigns={availableDesigns}
            availableTypes={availableTypes}
            availableBrands={availableBrands}
            availableRanges={availableRanges}
            availableMaterials={availableMaterials}
            availableStandards={availableStandards}
            availableCompositions={availableCompositions}
            availablePerformances={availablePerformances}
            availableSizes={availableSizes}
            availableShapes={availableShapes}
            availableApplications={availableApplications}
            filteredColours={filteredColours}
            filteredDesigns={filteredDesigns}
            filteredTypes={filteredTypes}
            filteredBrands={filteredBrands}
            filteredRanges={filteredRanges}
            filteredMaterials={filteredMaterials}
            filteredStandards={filteredStandards}
            filteredCompositions={filteredCompositions}
            filteredPerformances={filteredPerformances}
            filteredSizes={filteredSizes}
            filteredShapes={filteredShapes}
            colourList={colourList}
            designList={designList}
            typesList={typesList}
            brandList={brandList}
            rangeList={rangeList}
            materialList={materialList}
            standardsList={standardsList}
            compositionList={compositionList}
            performanceList={performanceList}
            sizeList={sizeList}
            shapeList={shapeList}
            setColourList={setColourList}
            setDesignList={setDesignList}
            setTypesList={setTypesList}
            setBrandList={setBrandList}
            setRangeList={setRangeList}
            setMaterialList={setMaterialList}
            setStandardsList={setStandardsList}
            setCompositionList={setCompositionList}
            setPerformanceList={setPerformanceList}
            setSizeList={setSizeList}
            setShapeList={setShapeList}
            handleCheckboxChange={handleCheckboxChange}
            applicationsList={applicationsList}
            setApplicationsList={setApplicationsList}
            applyFiltersForApplications={applyFiltersForApplications}
            itemGroup={''}
            isLoading={getProductsMutation.isLoading}
            showAvailability
            inStockOnly={inStockOnly}
            onStockChange={handleStockChange}
            showCategory
            selectedCategories={categoryList}
            availableCategories={availableCategories}
            onCategoryChange={handleCategoryChange}
            webTypeTitle="Types"
          />
        </Box>

        {/* Right: product grid */}
        <Box sx={{ minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box ref={anchorRef} id="global-search-filter-section" />
          {(getProductsMutation?.data?.products?.length === 0 || productItemCount === 0) && !getProductsMutation.isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                width: '100%',
                minHeight: '60vh',
              }}
            >
              <Typography fontSize={'1.5rem'} fontWeight={'bold'}>No items found</Typography>
              <Typography fontSize={'1.5rem'} fontWeight={'bold'}>Please try a different search or adjust your filters</Typography>
            </Box>
          ) : getProductsMutation.isLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                width: '100%',
                minHeight: '60vh',
              }}
            >
              <Typography fontSize={'1.5rem'} fontWeight={'bold'}>
                {searchQuery ? `Searching for "${searchQuery}"...` : 'Loading...'}
              </Typography>
            </Box>
          ) : (
            <ProductGrid
              productItemCount={productItemCount}
              getProductsMutation={getProductsMutation}
              totalPages={totalPages}
              currentPage={currentPage}
              previousPage={previousPage}
              nextPage={nextPage}
              lastPage={lastPage}
              firstPage={firstPage}
              anchorRef={anchorRef}
              showAll={showAll}
              accumulatedProducts={accumulatedProducts}
              onLoadMore={handleLoadMore}
              loadingMore={loadingMore}
              loadMoreFailed={loadMoreFailed}
              currentSort={sortBySetting}
              onSortChange={handleSortChange}
              currentItemsPerPage={itemsPerPage}
              onItemsPerPageChange={handleItemsPerPageChange}
              onProductNavigate={onClose}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default GlobalSearchFilterComponent;
